import React, { useEffect, useState } from "react";
import { Link2, RefreshCw, CheckCircle2, XCircle, MinusCircle, AlertTriangle, ExternalLink, Copy } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

const fmtDate = (iso) => { try { return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }); } catch { return "—"; } };

const StatusIcon = ({ ok }) => {
  if (ok === true) return <CheckCircle2 size={16} style={{ color: "#15803D" }} />;
  if (ok === false) return <XCircle size={16} style={{ color: "#B91C1C" }} />;
  return <MinusCircle size={16} style={{ color: "var(--yalola-text-muted)" }} />;
};

const ITEM_STYLE = { ok: "#15803D", fail: "#B91C1C", skip: "var(--yalola-text-muted)" };

const copy = async (txt) => { try { await navigator.clipboard.writeText(txt); toast.success("Lien copié"); } catch { toast.error("Copie impossible"); } };

const ItemRow = ({ it }) => {
  const [open, setOpen] = useState(it.status === "fail");
  return (
    <div className="rounded-xl border" style={{ borderColor: "var(--yalola-border)", borderLeft: `3px solid ${ITEM_STYLE[it.status]}` }} data-testid={`sharelink-item-${it.kind}`} data-status={it.status}>
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-3 px-3 py-2.5 text-left">
        <StatusIcon ok={it.status === "ok" ? true : it.status === "fail" ? false : null} />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold">{it.label}</div>
          <div className="text-xs truncate" style={{ color: "var(--yalola-text-muted)" }}>{it.og_url || it.checks?.[0]?.detail}</div>
        </div>
        {it.og_url && (
          <span className="flex items-center gap-1 shrink-0">
            <a href={it.og_url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="p-1.5 rounded-lg hover:bg-black/5" title="Ouvrir la page de partage" data-testid={`sharelink-open-${it.kind}`}><ExternalLink size={14} /></a>
            <span role="button" tabIndex={0} onClick={(e) => { e.stopPropagation(); copy(it.og_url); }} onKeyDown={(e) => { if (e.key === "Enter") copy(it.og_url); }} className="p-1.5 rounded-lg hover:bg-black/5 cursor-pointer" title="Copier le lien" data-testid={`sharelink-copy-${it.kind}`}><Copy size={14} /></span>
          </span>
        )}
      </button>
      {open && (
        <ul className="px-3 pb-3 space-y-1.5 text-xs" data-testid={`sharelink-checks-${it.kind}`}>
          {it.checks.map((c, i) => (
            <li key={i} className="flex items-start gap-2">
              <span className="mt-0.5"><StatusIcon ok={c.ok} /></span>
              <span><b>{c.name}</b> — <span style={{ color: "var(--yalola-text-muted)" }} className="break-all">{c.detail}</span></span>
            </li>
          ))}
          {it.web_url && <li className="pl-6" style={{ color: "var(--yalola-text-muted)" }}>Atterrissage : <span className="break-all">{it.web_url}</span></li>}
        </ul>
      )}
    </div>
  );
};

// Admin › Supervision › Liens de partage — live check of every share-link kind + app plumbing.
export const ShareLinksPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = async () => {
    setLoading(true);
    try { const { data: d } = await api.get("/admin/share-links/check"); setData(d); }
    catch (e) { toast.error(e?.friendlyMessage || "Diagnostic impossible"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  const s = data?.summary;
  const groups = [["android", "Android — App Links"], ["ios", "iOS — Universal Links"], ["web", "Web"]];
  return (
    <div className="space-y-4" data-testid="sharelinks-panel">
      <div className="yalola-card p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="font-bold font-display text-lg flex items-center gap-2"><Link2 size={18} /> Liens de partage</div>
            <p className="text-sm mt-1" style={{ color: "var(--yalola-text-muted)" }}>
              Chaque type de lien partagé (WhatsApp, Facebook, SMS…) est testé en direct : carte d'aperçu, image, atterrissage sur la bonne page et ouverture de l'app installée. Les liens s'écrivent toujours <code>{data?.base || ""}/api/e/…</code>.
            </p>
          </div>
          <Button type="button" variant="outline" size="sm" onClick={load} disabled={loading} data-testid="sharelinks-refresh">
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Relancer
          </Button>
        </div>
        {s && (
          <div className="flex flex-wrap gap-2 mt-3 text-xs" data-testid="sharelinks-summary" data-ok={s.ok} data-fail={s.fail}>
            <span className="px-2.5 py-1 rounded-full font-semibold" style={{ background: "#DCFCE7", color: "#15803D" }}>{s.ok} OK</span>
            <span className="px-2.5 py-1 rounded-full font-semibold" style={{ background: s.fail ? "#FEE2E2" : "var(--yalola-surface-alt)", color: s.fail ? "#B91C1C" : "var(--yalola-text-muted)" }}>{s.fail} en échec</span>
            <span className="px-2.5 py-1 rounded-full font-semibold" style={{ background: "var(--yalola-surface-alt)", color: "var(--yalola-text-muted)" }}>{s.skip} sans exemple</span>
            <span className="px-2.5 py-1 rounded-full" style={{ color: "var(--yalola-text-muted)" }}>Vérifié le {fmtDate(data.checked_at)}</span>
          </div>
        )}
      </div>

      <div className="yalola-card p-5 space-y-2">
        <div className="font-semibold mb-1">Types de liens</div>
        {!data && <div className="text-sm" style={{ color: "var(--yalola-text-muted)" }} data-testid="sharelinks-loading">{loading ? "Vérification en cours…" : "Aucun résultat"}</div>}
        {data?.items.map((it) => <ItemRow key={it.kind} it={it} />)}
      </div>

      <div className="yalola-card p-5">
        <div className="font-semibold mb-1">Ouverture de l'app installée</div>
        <p className="text-xs mb-3" style={{ color: "var(--yalola-text-muted)" }}>
          Un navigateur ne peut pas savoir si l'app est installée : ce sont Android (App Links) et iOS (Universal Links) qui ouvrent l'app directement quand ces fichiers sont valides. Sur le web mobile, une bannière « Ouvrir dans l'app Yalola » complète le dispositif.
        </p>
        {groups.map(([g, label]) => {
          const rows = (data?.platform || []).filter((p) => p.group === g);
          if (!rows.length) return null;
          return (
            <div key={g} className="mb-3" data-testid={`sharelinks-platform-${g}`}>
              <div className="text-xs font-bold uppercase tracking-wide mb-1" style={{ color: "var(--yalola-text-muted)" }}>{label}</div>
              <ul className="space-y-1.5 text-xs">
                {rows.map((p, i) => (
                  <li key={i} className="flex items-start gap-2" data-testid={`sharelinks-platform-row`} data-ok={p.ok === true ? "1" : p.ok === false ? "0" : ""}>
                    <span className="mt-0.5">{p.ok === null ? <AlertTriangle size={16} style={{ color: "#CA8A04" }} /> : <StatusIcon ok={p.ok} />}</span>
                    <span><b>{p.name}</b> — <span style={{ color: "var(--yalola-text-muted)" }} className="break-all">{p.detail}</span>
                      {Array.isArray(p.extra) && p.extra.length > 0 && (
                        <ul className="mt-1 space-y-0.5 font-mono text-[11px]" style={{ color: "var(--yalola-text-muted)" }}>{p.extra.map((fp) => <li key={fp} className="break-all">{fp}</li>)}</ul>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
        <div className="rounded-xl px-4 py-3 text-xs mt-2" style={{ background: "var(--yalola-surface-alt)" }} data-testid="sharelinks-howto">
          <b>Empreinte Google Play :</b> Play Console › ton app › <i>Test et publication › Configuration › Intégrité de l'app</i> › onglet <i>Signature de l'app</i> › « Certificat de clé de signature d'application » › <b>Empreinte du certificat SHA-256</b>. Elle doit figurer dans la liste ci-dessus (assetlinks.json), sinon Android n'ouvre pas l'app depuis les liens.
        </div>
      </div>
    </div>
  );
};

export default ShareLinksPanel;
